import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bot, Power, PowerOff, RefreshCw, Plus, Edit3, Trash2, 
  Copy, CheckCircle, AlertCircle, X, Key,
  Tag, Shield, Loader2, AlertTriangle, Info, RotateCcw, Bell
} from 'lucide-react';
import api from '../utils/api';
import { usePermissions } from '../hooks/usePermissions';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';

const TaskAgentSettings = ({ user }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [teamId, setTeamId] = useState(null);
  const [config, setConfig] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [rules, setRules] = useState([]);
  const [inboundLogs, setInboundLogs] = useState([]);
  const [decisionsLogs, setDecisionsLogs] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Use permissions hook
  const { hasPermission, isAdmin: permissionIsAdmin, role } = usePermissions();
  
  // UI State
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [activeTab, setActiveTab] = useState('status');
  const [logFilter, setLogFilter] = useState({ status: '', source: '' });
  
  // Modal States
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showLogDetail, setShowLogDetail] = useState(null);
  const [showSetupModal, setShowSetupModal] = useState(null); // trigger object or null
  
  // Form States
  const [triggerForm, setTriggerForm] = useState({ name: '', trigger_type: 'webhook', rate_limit_per_minute: 100, allowed_sources: [] });
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [newSecret, setNewSecret] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    category: '',
    required_fields: ['name'],
    optional_fields: [],
    missing_field_policy: 'needs_review',
    defaults: { priority: 'Normal' },
    is_active: true,
    priority_order: 0
  });
  const [editingRule, setEditingRule] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false); // Setup button loading state
  
  // Use permission-based admin check - rely on permissions system
  const isAdmin = permissionIsAdmin || hasPermission('ai.task_agent.configure');

  // Field options for rules
  const fieldOptions = [
    { value: 'name', label: 'Title' },
    { value: 'description', label: 'Description' },
    { value: 'priority', label: 'Priority' },
    { value: 'due_date', label: 'Due Date' },
    { value: 'estimated_effort_minutes', label: 'Estimated Effort' },
    { value: 'project_id', label: 'Project' },
    { value: 'client_id', label: 'Client' },
    { value: 'tags', label: 'Tags' },
    { value: 'assignees', label: 'Assignees' },
    { value: 'category', label: 'Category' }
  ];

  const priorityOptions = ['Low', 'Normal', 'High', 'Critical'];
  const policyOptions = [
    { value: 'needs_review', label: 'Route to Review' },
    { value: 'use_defaults', label: 'Use Defaults' },
    { value: 'reject', label: 'Reject Task' }
  ];

  // Fetch team ID (role is now from usePermissions hook)
  useEffect(() => {
    const fetchTeamId = async () => {
      try {
        const teamRes = await api.get('/team');
        setTeamId(teamRes.data.team_id);
      } catch (error) {
        console.error('Failed to fetch team:', error);
        setLoading(false);
      }
    };
    fetchTeamId();
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const [configRes, triggersRes, rulesRes, clientsRes] = await Promise.all([
        api.get(`/agent/config/${teamId}`).catch(() => ({ data: { configured: false } })),
        api.get(`/agent/triggers/${teamId}`).catch(() => ({ data: { triggers: [] } })),
        api.get(`/agent/config/${teamId}/rules`).catch(() => ({ data: { rules: [] } })),
        api.get('/clients').catch(() => ({ data: [] }))
      ]);
      
      setConfig(configRes.data);
      setTriggers(triggersRes.data.triggers || []);
      setRules(rulesRes.data.rules || []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
    } catch (error) {
      console.error('Failed to fetch agent data:', error);
      showNotification('error', 'Failed to load agent configuration');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    if (!teamId || !config?.configured) return;
    try {
      const params = new URLSearchParams();
      params.append('limit', '50');
      if (logFilter.status) params.append('inbound_status', logFilter.status);
      
      const [inboundRes, decisionsRes] = await Promise.all([
        api.get(`/agent/inbound/${teamId}?${params}`).catch(() => ({ data: { inbound: [] } })),
        api.get(`/agent/decisions/${teamId}?limit=50`).catch(() => ({ data: { decisions: [] } }))
      ]);
      
      setInboundLogs(inboundRes.data.inbound || []);
      setDecisionsLogs(decisionsRes.data.decisions || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  }, [teamId, config?.configured, logFilter]);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab, fetchLogs]);

  // Notification helper
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 4000);
  };

  // Setup handler (was "bootstrap")
  const handleSetup = async () => {
    setSetupLoading(true);
    try {
      console.log('[TaskAgent] Calling setup endpoint for team:', teamId);
      const response = await api.post(`/agent/config/${teamId}/setup`);
      console.log('[TaskAgent] Setup response:', response.data);
      
      if (response.data.already_configured) {
        showNotification('success', 'Task Agent is already set up');
      } else {
        showNotification('success', 'Task Agent set up successfully');
      }
      
      // Refresh data and switch to Status tab to show config
      await fetchData();
      setActiveTab('status');
    } catch (error) {
      // Log full error for debugging
      console.error('[TaskAgent] Setup failed:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Show user-friendly error
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      let errorMessage = 'Failed to set up Task Agent';
      if (status === 401) {
        errorMessage = 'Session expired. Please refresh the page and log in again.';
      } else if (status === 403) {
        errorMessage = detail || 'You need admin or owner access to set up Task Agent.';
      } else if (status === 404) {
        errorMessage = 'Setup endpoint not found. Please contact support.';
      } else if (status === 422) {
        errorMessage = detail?.msg || detail || 'Invalid request. Please try again.';
      } else if (status === 500) {
        errorMessage = 'Server error. Please try again or contact support.';
      } else if (detail) {
        errorMessage = typeof detail === 'string' ? detail : detail.message || errorMessage;
      }
      
      showNotification('error', errorMessage);
    } finally {
      setSetupLoading(false);
    }
  };

  // Enable/Disable handlers
  const handleEnable = async () => {
    try {
      const response = await api.post(`/agent/config/${teamId}/enable`);
      if (response.data.status === 'success') {
        showNotification('success', 'Task Agent enabled');
        fetchData();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'AGENT_NOT_READY') {
        // Update config with the checks from the error response
        setConfig(prev => ({ ...prev, can_enable: false, checks: detail.checks }));
        showNotification('error', 'Agent not ready. Complete the prerequisites below.');
      } else {
        showNotification('error', detail?.message || detail || 'Failed to enable agent');
      }
    }
  };

  const handleDisable = async () => {
    try {
      await api.post(`/agent/config/${teamId}/disable`);
      showNotification('success', 'Task Agent disabled');
      fetchData();
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to disable agent');
    }
  };

  // Notification settings update
  const handleUpdateNotifications = async (updates) => {
    try {
      await api.put(`/agent/config/${teamId}`, updates);
      showNotification('success', 'Settings updated');
      fetchData();
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to update settings');
    }
  };

  // Trigger handlers
  const handleCreateTrigger = async () => {
    try {
      const response = await api.post(`/agent/triggers/${teamId}`, triggerForm);
      setNewSecret(response.data.secret);
      setShowSecretModal(true);
      setShowTriggerModal(false);
      setTriggerForm({ name: '', trigger_type: 'webhook', rate_limit_per_minute: 100, allowed_sources: [] });
      fetchData();
      showNotification('success', 'Trigger created');
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to create trigger');
    }
  };

  const handleUpdateTrigger = async () => {
    try {
      await api.put(`/agent/triggers/${editingTrigger.id}`, triggerForm);
      setShowTriggerModal(false);
      setEditingTrigger(null);
      setTriggerForm({ name: '', trigger_type: 'webhook', rate_limit_per_minute: 100, allowed_sources: [] });
      fetchData();
      showNotification('success', 'Trigger updated');
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to update trigger');
    }
  };

  const handleRotateSecret = async (triggerId) => {
    try {
      const response = await api.post(`/agent/triggers/${triggerId}/rotate`);
      setNewSecret(response.data.secret);
      setShowSecretModal(true);
      showNotification('success', `Secret rotated. Old secret valid for ${response.data.grace_period_hours} hours.`);
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to rotate secret');
    }
  };

  const handleDeleteTrigger = async (triggerId) => {
    try {
      await api.delete(`/agent/triggers/${triggerId}`);
      fetchData();
      showNotification('success', 'Trigger deleted');
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to delete trigger');
    }
  };

  // Rule handlers
  const handleSaveRule = async () => {
    try {
      await api.post(`/agent/config/${teamId}/rules`, ruleForm);
      setShowRuleModal(false);
      setEditingRule(null);
      setRuleForm({
        category: '',
        required_fields: ['name'],
        optional_fields: [],
        missing_field_policy: 'needs_review',
        defaults: { priority: 'Normal' },
        is_active: true,
        priority_order: 0
      });
      fetchData();
      showNotification('success', editingRule ? 'Rule updated' : 'Rule created');
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDeleteRule = async (category) => {
    try {
      await api.delete(`/agent/config/${teamId}/rules/${encodeURIComponent(category)}`);
      fetchData();
      showNotification('success', 'Rule deleted');
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to delete rule');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification('success', 'Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-3 text-gray-600">Loading Task Agent settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          notification.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {notification.message}
          <button onClick={() => setNotification({ show: false, type: '', message: '' })}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bot className="w-8 h-8 text-purple-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Task Creation Agent</h2>
            <p className="text-sm text-gray-500">Automate task creation from webhooks and emails</p>
          </div>
        </div>
        {config?.configured && (
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            config.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
          }`}>
            {config.enabled ? 'Enabled' : 'Disabled'}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="status" className="data-[state=active]:bg-white">Status</TabsTrigger>
          <TabsTrigger value="triggers" className="data-[state=active]:bg-white">Triggers</TabsTrigger>
          <TabsTrigger value="rules" className="data-[state=active]:bg-white">Rules</TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-white">Logs</TabsTrigger>
        </TabsList>

        {/* STATUS TAB */}
        <TabsContent value="status" className="space-y-6 mt-6">
          {!config?.configured ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
              <Bot className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-amber-800 mb-2">Set up Task Agent</h3>
              <p className="text-amber-700 mb-6 max-w-md mx-auto">
                Task Agent isn't set up yet. We'll create an Intake Project and default settings so you can start configuring triggers and rules.
              </p>
              {isAdmin ? (
                <div className="space-y-3">
                  <button
                    onClick={handleSetup}
                    disabled={setupLoading}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    data-testid="setup-task-agent-btn"
                  >
                    {setupLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        <Bot className="w-4 h-4" />
                        Set up Task Agent
                      </>
                    )}
                  </button>
                  <p className="text-sm text-amber-600">Only workspace admins can set this up.</p>
                </div>
              ) : (
                <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-amber-800 font-medium">
                    Ask an admin to set up Task Agent.
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    You don't have permission to configure this feature.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Readiness Checklist */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  Readiness Checklist
                </h3>
                <div className="space-y-3">
                  {config.checks?.map((check) => (
                    <div key={check.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {check.passed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className={check.passed ? 'text-gray-700' : 'text-red-700 font-medium'}>
                          {check.label}
                        </span>
                      </div>
                      {!check.passed && isAdmin && (
                        <button
                          onClick={() => {
                            if (check.key === 'has_active_trigger') setActiveTab('triggers');
                            else if (check.key === 'has_active_rules') setActiveTab('rules');
                            else if (check.key.includes('intake') || check.key.includes('stage')) handleSetup();
                          }}
                          className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                        >
                          {check.key === 'has_active_trigger' ? 'Create Trigger →' :
                           check.key === 'has_active_rules' ? 'Create Rule →' :
                           'Fix →'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Enable/Disable Controls */}
              {isAdmin && (
                <div className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Power className="w-5 h-5 text-purple-600" />
                    Agent Control
                  </h3>
                  <div className="flex items-center gap-4">
                    {config.enabled ? (
                      <button
                        onClick={handleDisable}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      >
                        <PowerOff className="w-4 h-4" />
                        Disable Agent
                      </button>
                    ) : (
                      <button
                        onClick={handleEnable}
                        disabled={!config.can_enable}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          config.can_enable
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <Power className="w-4 h-4" />
                        Enable Agent
                      </button>
                    )}
                  </div>
                  {!config.can_enable && !config.enabled && (
                    <p className="text-sm text-gray-500 mt-2">Complete all checklist items to enable the agent.</p>
                  )}
                </div>
              )}

              {/* Notification Settings */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-purple-600" />
                  Notification Settings
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-700">Notify on Review</p>
                      <p className="text-sm text-gray-500">Send notifications when tasks need human review</p>
                    </div>
                    <Switch
                      checked={config.notify_on_review}
                      onCheckedChange={(checked) => isAdmin && handleUpdateNotifications({ notify_on_review: checked })}
                      disabled={!isAdmin}
                    />
                  </div>
                </div>
              </div>

              {/* Configuration Info */}
              <div className="bg-gray-50 border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5 text-gray-600" />
                  How It Works
                </h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <p>
                    <strong>Client Detection:</strong> Automatic using contact email and phone from your client database. 
                    No manual configuration required.
                  </p>
                  <p>
                    <strong>Detection Order:</strong> Explicit IDs → Contact email → Contact phone → Contact name (exact match only)
                  </p>
                  <p>
                    <strong>When detection fails:</strong> Tasks are routed to the Intake Project for human review.
                  </p>
                </div>
                <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Intake Project ID:</span>
                    <span className="ml-2 font-mono text-gray-700 text-xs">{config.intake_project_id?.slice(0, 8) || 'Not set'}...</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Review Stage ID:</span>
                    <span className="ml-2 font-mono text-gray-700 text-xs">{config.needs_review_stage_id?.slice(0, 8) || 'Not set'}...</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* TRIGGERS TAB */}
        <TabsContent value="triggers" className="space-y-6 mt-6">
          {/* Webhook URL - Always visible */}
          <div className="bg-gray-900 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-400">Webhook URL</h4>
              <button
                onClick={() => copyToClipboard(`${window.location.origin}/api/agent/inbound/webhook`)}
                className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
            </div>
            <code className="text-green-400 text-sm break-all">
              {window.location.origin}/api/agent/inbound/webhook
            </code>
            <p className="text-xs text-gray-500 mt-2">
              All external systems POST to this same URL. Your trigger key identifies the source.
            </p>
          </div>

          {/* Header + Create Button */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Triggers</h3>
              <p className="text-sm text-gray-500">
                {triggers.length === 0 
                  ? "Create your first webhook trigger to start receiving tasks"
                  : "Each trigger is an API key. Most users only need one."}
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={() => {
                  setEditingTrigger(null);
                  setTriggerForm({ name: '', trigger_type: 'webhook', rate_limit_per_minute: 100, allowed_sources: [] });
                  setShowTriggerModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {triggers.length === 0 ? "Create Your First Trigger" : "Add Another Trigger"}
              </button>
            )}
          </div>

          {/* Triggers List */}
          <div className="space-y-4">
            {triggers.length === 0 ? (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="font-medium text-gray-700 mb-2">No triggers yet</h4>
                <p className="text-sm text-gray-500 mb-4">
                  Create a trigger to get an API key for your external systems (Zapier, HighLevel, etc.)
                </p>
              </div>
            ) : (
              triggers.map((trigger) => (
                <div key={trigger.id} className="bg-white border rounded-lg overflow-hidden">
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${trigger.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <Key className={`w-5 h-5 ${trigger.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{trigger.name}</h4>
                        <p className="text-sm text-gray-500">
                          Rate limit: {trigger.rate_limit_per_minute}/min • Key version: {trigger.secret_version}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        trigger.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {trigger.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => setShowSetupModal(trigger)}
                        className="px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      >
                        Setup
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => {
                              setEditingTrigger(trigger);
                              setTriggerForm({
                                name: trigger.name,
                                trigger_type: trigger.trigger_type,
                                rate_limit_per_minute: trigger.rate_limit_per_minute,
                                allowed_sources: trigger.allowed_sources || [],
                                is_active: trigger.is_active
                              });
                              setShowTriggerModal(true);
                            }}
                            className="p-2 text-gray-500 hover:text-gray-700 rounded"
                            title="Edit trigger"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRotateSecret(trigger.id)}
                            className="p-2 text-gray-500 hover:text-orange-600 rounded"
                            title="Rotate secret (generates new key)"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button className="p-2 text-gray-500 hover:text-red-600 rounded" title="Delete trigger">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Trigger</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete &quot;{trigger.name}&quot;? This will invalidate the API key and stop all incoming webhooks using it.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteTrigger(trigger.id)} className="bg-red-600 hover:bg-red-700">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Helper text */}
          {triggers.length > 0 && (
            <p className="text-xs text-gray-500 text-center">
              Use multiple triggers to track different sources separately, or just use one for everything.
            </p>
          )}
        </TabsContent>

        {/* RULES TAB */}
        <TabsContent value="rules" className="space-y-6 mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Category Rules</h3>
            {isAdmin && (
              <button
                onClick={() => {
                  setEditingRule(null);
                  setRuleForm({
                    category: '',
                    required_fields: ['name'],
                    optional_fields: [],
                    missing_field_policy: 'needs_review',
                    defaults: { priority: 'Normal' },
                    is_active: true,
                    priority_order: 0
                  });
                  setShowRuleModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Rule
              </button>
            )}
          </div>

          {/* Rules List */}
          <div className="space-y-4">
            {rules.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No category rules configured. Create one to define task handling behavior.
              </div>
            ) : (
              rules.map((rule) => (
                <div key={rule.id} className={`bg-white border rounded-lg p-4 ${
                  rule.category === 'Uncategorized' ? 'border-purple-200 bg-purple-50/50' : ''
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Tag className="w-5 h-5 text-purple-600" />
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {rule.category}
                          {rule.category === 'Uncategorized' && (
                            <span className="ml-2 text-xs text-purple-600 font-normal">(Fallback)</span>
                          )}
                        </h4>
                        <p className="text-sm text-gray-500">
                          Required: {rule.required_fields?.join(', ') || 'none'} • 
                          Policy: {rule.missing_field_policy} •
                          Priority: {rule.priority_order}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        rule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => {
                              setEditingRule(rule);
                              setRuleForm({
                                category: rule.category,
                                required_fields: rule.required_fields || ['name'],
                                optional_fields: rule.optional_fields || [],
                                missing_field_policy: rule.missing_field_policy || 'needs_review',
                                defaults: rule.defaults || { priority: 'Normal' },
                                is_active: rule.is_active,
                                priority_order: rule.priority_order || 0
                              });
                              setShowRuleModal(true);
                            }}
                            className="p-2 text-gray-500 hover:text-gray-700"
                            title="Edit rule"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {rule.category !== 'Uncategorized' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button className="p-2 text-gray-500 hover:text-red-600" title="Delete rule">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Rule</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete the &quot;{rule.category}&quot; rule?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteRule(rule.category)} className="bg-red-600 hover:bg-red-700">
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* LOGS TAB */}
        <TabsContent value="logs" className="space-y-6 mt-6">
          <Tabs defaultValue="inbound" className="w-full">
            <TabsList className="bg-gray-100">
              <TabsTrigger value="inbound">Inbound Log</TabsTrigger>
              <TabsTrigger value="decisions">Decisions Log</TabsTrigger>
            </TabsList>

            <TabsContent value="inbound" className="mt-4">
              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <select
                  value={logFilter.status}
                  onChange={(e) => setLogFilter(prev => ({ ...prev, status: e.target.value }))}
                  className="px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="duplicate">Duplicate</option>
                  <option value="dead">Dead</option>
                </select>
                <button onClick={fetchLogs} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {/* Inbound Table */}
              <div className="bg-white border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Attempts</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Task</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {inboundLogs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                          No inbound logs found
                        </td>
                      </tr>
                    ) : (
                      inboundLogs.map((log) => (
                        <tr 
                          key={log.id} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => setShowLogDetail(log)}
                        >
                          <td className="px-4 py-3 text-gray-600">
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-700">{log.source}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              log.status === 'completed' ? 'bg-green-100 text-green-700' :
                              log.status === 'failed' || log.status === 'dead' ? 'bg-red-100 text-red-700' :
                              log.status === 'duplicate' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {log.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{log.attempts}</td>
                          <td className="px-4 py-3">
                            {log.task_id && (
                              <a 
                                href={`/tasks/${log.task_id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-purple-600 hover:underline font-mono text-xs"
                              >
                                {log.task_id.slice(0, 8)}...
                              </a>
                            )}
                          </td>
                          <td className="px-4 py-3 text-red-600 text-xs truncate max-w-xs">
                            {log.error_message}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="decisions" className="mt-4">
              {/* Decisions Table */}
              <div className="bg-white border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Time</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Category</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Client</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Project</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Confidence</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Review</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {decisionsLogs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                          No decision logs found
                        </td>
                      </tr>
                    ) : (
                      decisionsLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-600">
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-700">{log.detected_category}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {log.detected_client_id ? log.detected_client_id.slice(0, 8) + '...' : '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {log.detected_project_id ? log.detected_project_id.slice(0, 8) + '...' : '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {log.confidence_scores?.context ? `${(log.confidence_scores.context * 100).toFixed(0)}%` : '-'}
                          </td>
                          <td className="px-4 py-3">
                            {log.routed_to_review ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                                Yes
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                                No
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* TRIGGER MODAL */}
      <Dialog open={showTriggerModal} onOpenChange={setShowTriggerModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTrigger ? 'Edit Trigger' : 'Create Trigger'}</DialogTitle>
            <DialogDescription>
              {editingTrigger ? 'Update trigger settings' : 'Create a new webhook trigger to receive tasks'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={triggerForm.name}
                onChange={(e) => setTriggerForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Zapier Integration"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rate Limit (per minute)</label>
              <input
                type="number"
                value={triggerForm.rate_limit_per_minute}
                onChange={(e) => setTriggerForm(prev => ({ ...prev, rate_limit_per_minute: parseInt(e.target.value) || 100 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            {editingTrigger && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Active</span>
                <Switch
                  checked={triggerForm.is_active}
                  onCheckedChange={(checked) => setTriggerForm(prev => ({ ...prev, is_active: checked }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowTriggerModal(false)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={editingTrigger ? handleUpdateTrigger : handleCreateTrigger}
              disabled={!triggerForm.name}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {editingTrigger ? 'Update' : 'Create'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SECRET MODAL - Shows after creating trigger or rotating */}
      <Dialog open={showSecretModal} onOpenChange={setShowSecretModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Save Your API Key Now
            </DialogTitle>
            <DialogDescription>
              This secret will only be shown once. Copy and store it securely.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Webhook URL */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Webhook URL</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm text-gray-700 break-all">
                  {window.location.origin}/api/agent/inbound/webhook
                </code>
                <button
                  onClick={() => copyToClipboard(`${window.location.origin}/api/agent/inbound/webhook`)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Header */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Header</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm text-gray-700">
                  X-Agent-Key: <span className="text-purple-600">[your-secret]</span>
                </code>
              </div>
            </div>

            {/* Secret */}
            <div>
              <label className="block text-xs font-medium text-orange-600 mb-1">Your Secret (shown once!)</label>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm break-all">
                {newSecret}
              </div>
            </div>

            <button
              onClick={() => copyToClipboard(newSecret)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Copy className="w-4 h-4" />
              Copy Secret to Clipboard
            </button>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm text-orange-800">
                <strong>Important:</strong> This secret cannot be retrieved later. If lost, use &quot;Rotate Secret&quot; to generate a new one.
              </p>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => {
                setShowSecretModal(false);
                setNewSecret(null);
              }}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            >
              I&apos;ve Saved the Secret
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SETUP MODAL - Shows setup instructions for existing trigger */}
      <Dialog open={!!showSetupModal} onOpenChange={() => setShowSetupModal(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Setup Instructions</DialogTitle>
            <DialogDescription>
              Configure your external system to send webhooks to Athena
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Webhook URL */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">1. Webhook URL</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm text-gray-700 break-all">
                  {window.location.origin}/api/agent/inbound/webhook
                </code>
                <button
                  onClick={() => copyToClipboard(`${window.location.origin}/api/agent/inbound/webhook`)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Header */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">2. Add Header</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm text-gray-700">
                  X-Agent-Key: <span className="text-purple-600">[your-secret-from-creation]</span>
                </code>
              </div>
            </div>

            {/* Explanation */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> All external systems POST to the same webhook URL. 
                Your API key in the header identifies which trigger (and team) the request belongs to.
              </p>
              <p className="text-sm text-blue-700">
                This trigger: <strong>{showSetupModal?.name}</strong> (v{showSetupModal?.secret_version})
              </p>
            </div>

            {/* Lost secret? */}
            <div className="bg-gray-50 border rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-1">Lost your secret?</h4>
              <p className="text-sm text-gray-600 mb-2">
                Secrets cannot be retrieved after creation. Use &quot;Rotate Secret&quot; to generate a new one. 
                The old key will remain valid for 24 hours.
              </p>
              {isAdmin && (
                <button
                  onClick={() => {
                    setShowSetupModal(null);
                    handleRotateSecret(showSetupModal?.id);
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  Rotate Secret →
                </button>
              )}
            </div>

            {/* Example payload */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Example Payload</label>
              <pre className="bg-gray-900 text-gray-300 p-3 rounded-lg text-xs overflow-x-auto">
{`{
  "source": "zapier",
  "title": "New task from webhook",
  "description": "Task details here",
  "contact_email": "client@example.com",
  "priority": "High"
}`}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowSetupModal(null)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RULE MODAL */}
      <Dialog open={showRuleModal} onOpenChange={setShowRuleModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Category Rule'}</DialogTitle>
            <DialogDescription>
              Define how tasks with this category should be handled
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
              <input
                type="text"
                value={ruleForm.category}
                onChange={(e) => setRuleForm(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Client Request"
                disabled={editingRule?.category === 'Uncategorized'}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Required Fields</label>
              <div className="flex flex-wrap gap-2">
                {fieldOptions.map(field => (
                  <label key={field.value} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={ruleForm.required_fields.includes(field.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRuleForm(prev => ({ ...prev, required_fields: [...prev.required_fields, field.value] }));
                        } else {
                          setRuleForm(prev => ({ ...prev, required_fields: prev.required_fields.filter(f => f !== field.value) }));
                        }
                      }}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    {field.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Missing Field Policy</label>
              <select
                value={ruleForm.missing_field_policy}
                onChange={(e) => setRuleForm(prev => ({ ...prev, missing_field_policy: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {policyOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Priority</label>
              <select
                value={ruleForm.defaults?.priority || 'Normal'}
                onChange={(e) => setRuleForm(prev => ({ ...prev, defaults: { ...prev.defaults, priority: e.target.value } }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {priorityOptions.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority Order</label>
              <input
                type="number"
                value={ruleForm.priority_order}
                onChange={(e) => setRuleForm(prev => ({ ...prev, priority_order: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers = higher priority in rule matching</p>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Active</span>
              <Switch
                checked={ruleForm.is_active}
                onCheckedChange={(checked) => setRuleForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowRuleModal(false)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveRule}
              disabled={!ruleForm.category}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {editingRule ? 'Update' : 'Create'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LOG DETAIL DRAWER */}
      {showLogDetail && (
        <Dialog open={!!showLogDetail} onOpenChange={() => setShowLogDetail(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Inbound Log Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">ID:</span>
                  <span className="ml-2 font-mono">{showLogDetail.id}</span>
                </div>
                <div>
                  <span className="text-gray-500">Source:</span>
                  <span className="ml-2">{showLogDetail.source}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <span className="ml-2">{showLogDetail.status}</span>
                </div>
                <div>
                  <span className="text-gray-500">Attempts:</span>
                  <span className="ml-2">{showLogDetail.attempts}</span>
                </div>
              </div>
              {showLogDetail.error_message && (
                <div>
                  <h4 className="font-medium text-red-700 mb-1">Error</h4>
                  <pre className="bg-red-50 p-3 rounded text-sm text-red-800 overflow-x-auto">
                    {showLogDetail.error_message}
                  </pre>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TaskAgentSettings;
